if (!customElements.get('addon-upsell')) {
  customElements.define(
    'addon-upsell',
    class AddonUpsell extends HTMLElement {
      constructor() {
        super();
        this.productHandle = this.dataset.productHandle;
        this.formId = this.dataset.formId;
        this.addonsUrl = this.dataset.addonsUrl;
        this.selected = new Map();
        this.rowInstanceId = 0;

        // Optional overrides used by callers (like the Mount Finder) that
        // already know exactly which add-on handles to show, rather than
        // looking them up from assets/addons.json by the current product's
        // handle. Product pages never set these, so their behavior is
        // unchanged.
        this.explicitHandles = this.dataset.addonHandles
          ? this.dataset.addonHandles.split(',').map((h) => h.trim()).filter(Boolean)
          : null;
        this.requiredHandles = new Set(
          (this.dataset.requiredHandles || '').split(',').map((h) => h.trim()).filter(Boolean)
        );
        this.preselectMap = {};
        if (this.dataset.preselectMap) {
          try {
            this.preselectMap = JSON.parse(this.dataset.preselectMap) || {};
          } catch (error) {
            console.error('Add-on upsell: failed to parse data-preselect-map', error);
          }
        }

        this.init();
      }

      async init() {
        try {
          this.embeddedVariants = this.readEmbeddedVariants();

          let handles = this.explicitHandles;
          if (!handles) {
            const res = await fetch(this.addonsUrl);
            if (!res.ok) return;
            const map = await res.json();
            handles = map[this.productHandle];
            console.log('Add-on upsell: resolved product handle', this.productHandle, '-> addons.json entry', handles);
          }
          if (!Array.isArray(handles) || handles.length === 0) return;

          const products = (await Promise.all(handles.map((handle) => this.fetchProduct(handle)))).filter(Boolean);
          if (products.length === 0) return;

          this.renderList(products);
          this.removeAttribute('hidden');
          this.hookForm();
        } catch (error) {
          console.error('Add-on upsell: failed to initialize', error);
        }
      }

      // Server-rendered variant data embedded by the Liquid snippet from
      // all_products[handle], as { id, options: [{name, value}, ...] } per
      // variant. This is the ONLY source of truth for matching a selection to
      // a variant id — values pass through Liquid's `| json` filter, which
      // safely escapes quotes and other special characters. The JS never
      // re-serializes these into HTML attributes and reads them back; that
      // round-trip is what previously corrupted values containing a literal
      // `"` character (e.g. 15/40 Series (1.5")).
      readEmbeddedVariants() {
        const script = document.querySelector('[data-addon-variants]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent) || {};
        } catch (error) {
          console.error('Add-on upsell: failed to parse embedded variant data', error);
          return {};
        }
      }

      async fetchProduct(handle) {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          return null;
        }
      }

      formatMoney(cents) {
        const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
        try {
          return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2)} ${currency}`;
        }
      }

      // Universal HTML escaper, safe for both text-node content AND
      // double-quoted attribute values. A previous textContent/innerHTML
      // round-trip only escaped &, <, > — browsers don't require escaping `"`
      // in text-node content, so it silently let literal quote characters
      // through. That broke every place this was used to build an HTML
      // attribute (value="...", title="..."), since an unescaped `"`
      // prematurely closes the attribute and corrupts everything after it.
      escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      // Shopify's /products/{handle}.js can return option names/values as plain
      // strings or as {name, ...} objects (e.g. when backed by a linked metafield
      // like Color swatches). Always resolve down to a plain, trimmed display
      // string — trimming matters because stray whitespace on a variant's option
      // value ("6 Meter " vs "6 Meter") would otherwise make an exact-match
      // comparison silently fail.
      optionLabel(value) {
        if (value && typeof value === 'object') {
          return value.name != null ? String(value.name).trim() : '';
        }
        return value == null ? '' : String(value).trim();
      }

      colorSwatchHex(name) {
        return window.StrikerVariantPicker.colorSwatchHex(name);
      }

      renderList(products) {
        const list = this.querySelector('[data-addon-list]');
        if (!list) return;
        list.innerHTML = '';

        if (this.requiredHandles.size === 0) {
          // No caller-specified required set: preserve the original flat
          // list with no group headings, exactly as on product pages.
          products.forEach((product) => list.appendChild(this.buildRow(product)));
          return;
        }

        const requiredProducts = products.filter((product) => this.requiredHandles.has(product.handle));
        const optionalProducts = products.filter((product) => !this.requiredHandles.has(product.handle));

        if (requiredProducts.length > 0) {
          list.appendChild(this.groupHeadingEl('Required for your setup'));
          requiredProducts.forEach((product) => list.appendChild(this.buildRow(product)));
        }
        if (optionalProducts.length > 0) {
          list.appendChild(this.groupHeadingEl('Optional add-ons'));
          optionalProducts.forEach((product) => list.appendChild(this.buildRow(product)));
        }
      }

      groupHeadingEl(text) {
        const heading = document.createElement('h4');
        heading.className = 'addon-upsell__group-heading';
        heading.textContent = text;
        return heading;
      }

      // A value at `optionIndex` is offered if at least one variant carrying that
      // value is purchasable, independent of the other options currently picked.
      valueIsAvailable(variants, optionIndex, value) {
        return variants.some((v) => v._labels[optionIndex] === value && v.available);
      }

      findExactVariant(variants, selection) {
        return variants.find((v) => v._labels.every((val, i) => val === selection[i]));
      }

      buildRow(product) {
        const instanceId = ++this.rowInstanceId;
        const variants = product.variants || [];

        // Prefer the Liquid-embedded, guaranteed-clean option values for each
        // variant (matched by id, as {name, value} pairs); fall back to the
        // AJAX-fetched data (still trimmed/normalized via optionLabel) when a
        // variant has no embedded counterpart, e.g. an add-on not yet listed
        // in the snippet's handle list.
        const embeddedForProduct = (this.embeddedVariants && this.embeddedVariants[product.handle]) || [];
        const embeddedById = new Map(embeddedForProduct.map((v) => [Number(v.id), v.options]));

        variants.forEach((v) => {
          const embeddedOptions = embeddedById.get(Number(v.id));
          v._labels = embeddedOptions
            ? embeddedOptions.map((opt) => this.optionLabel(opt && opt.value))
            : (v.options || []).map((val) => this.optionLabel(val));
        });

        if (embeddedForProduct.length === 0) {
          console.warn(
            `Add-on upsell: "${product.handle}" has no embedded variant data — add its handle to the addon_catalog_handles list in snippets/addon-upsell.liquid for more reliable matching. Falling back to AJAX data.`
          );
        }

        const rawOptionGroups = (product.options || []).map((opt) => this.optionLabel(opt));
        const hasOnlyDefault =
          variants.length <= 1 || (rawOptionGroups.length === 1 && rawOptionGroups[0].toLowerCase() === 'title');
        const anyAvailable = variants.some((v) => v.available);
        const firstAvailable = variants.find((v) => v.available) || variants[0];

        const isRequired = this.requiredHandles.has(product.handle);
        const preselectValue = this.preselectMap[product.handle];

        let added = false;
        let selection = firstAvailable._labels.slice();
        let currentVariant = firstAvailable;

        // A required add-on (e.g. steel discs on a non-magnetic roof) or one
        // the caller asked to preselect by option value (e.g. matching the
        // customer's already-chosen crossbar series) starts already added,
        // so it's included in the batched cart add without the customer
        // having to touch it.
        // preselectValue may be a single literal option value (legacy callers)
        // or an array of values to match across MULTIPLE option dimensions at
        // once — e.g. T-Nuts needs both Qty ("4") and Series ("Series 10 80/20
        // (1\")") set simultaneously, which a single string can't express.
        if (preselectValue && !hasOnlyDefault) {
          const wantedValues = Array.isArray(preselectValue) ? preselectValue : [preselectValue];
          const preselectedSelection = firstAvailable._labels.slice();
          wantedValues.forEach((wanted) => {
            const idx = rawOptionGroups.findIndex((_, i) => variants.some((v) => v._labels[i] === wanted));
            if (idx !== -1) preselectedSelection[idx] = wanted;
          });
          const match =
            variants.find((v) => v._labels.every((val, i) => val === preselectedSelection[i])) ||
            variants.find((v) => wantedValues.every((wanted) => v._labels.includes(wanted)) && v.available) ||
            variants.find((v) => wantedValues.every((wanted) => v._labels.includes(wanted)));
          if (match) {
            currentVariant = match;
            selection = match._labels.slice();
            added = true;
          }
        } else if (isRequired) {
          added = true;
        }

        // Computed once and reused for both rendering and the change-handler's
        // lookups, so a selected pill's actual value is always read back from
        // this in-memory array (sourced from the embedded JSON) rather than
        // from a DOM attribute — the DOM is never the source of truth for
        // matching, which is what previously let quote characters corrupt
        // selections.
        const groups = hasOnlyDefault
          ? [{ name: '', index: 0, values: ['Add'] }]
          : rawOptionGroups.map((name, index) => ({
              name,
              index,
              values: [...new Set(variants.map((v) => v._labels[index]))],
            }));

        const row = document.createElement('div');
        row.className = 'addon-upsell__row';
        if (!anyAvailable) row.classList.add('addon-upsell__row--soldout');
        if (isRequired) row.classList.add('addon-upsell__row--required');

        row.innerHTML = `
          <div class="addon-upsell__header">
            <span class="addon-upsell__title">
              ${isRequired ? '<span class="addon-upsell__required-badge">Add this too</span>' : ''}
              <a
                class="addon-upsell__title-link"
                href="${this.escapeHtml(product.url)}"
                target="_blank"
                rel="noopener"
                >${this.escapeHtml(product.title)}</a
              >
            </span>
            <span class="addon-upsell__price" data-addon-price></span>
          </div>
          <div class="addon-upsell__options" data-addon-options></div>
        `;

        const priceEl = row.querySelector('[data-addon-price]');
        const optionsWrap = row.querySelector('[data-addon-options]');

        const applySelection = () => {
          if (added && currentVariant && currentVariant.available) {
            if (!currentVariant.id || !Number.isFinite(Number(currentVariant.id))) {
              console.error(
                `Add-on upsell: "${product.handle}" resolved to an invalid variant id — not adding to cart.`,
                currentVariant
              );
              this.selected.delete(product.handle);
              return;
            }
            this.selected.set(product.handle, currentVariant.id);
          } else {
            this.selected.delete(product.handle);
          }
        };

        // Select-to-add: there's no separate "add" toggle. Picking a variant
        // pill both selects it AND marks the accessory as added; clicking the
        // already-selected pill again deselects it, which is the sole way to
        // remove the accessory. Nothing is ever pre-selected on load.
        const updatePriceDisplay = () => {
          if (added && currentVariant) {
            const price = Number(currentVariant.price);
            const compareAt = Number(currentVariant.compare_at_price);
            const hasCompareAt = compareAt > price;
            if (hasCompareAt) {
              priceEl.innerHTML = `<s class="addon-upsell__price-compare">${this.formatMoney(
                compareAt
              )}</s><span class="addon-upsell__price-sale">${this.formatMoney(price)}</span>`;
            } else {
              priceEl.textContent = this.formatMoney(price);
            }
            priceEl.classList.remove('addon-upsell__price--not-added');
          } else {
            priceEl.textContent = 'Not added';
            priceEl.classList.add('addon-upsell__price--not-added');
          }
        };

        const renderGroup = (group) =>
          window.StrikerVariantPicker.renderOptionGroup(
            { name: group.name, groupIndex: group.index, values: group.values },
            {
              idPrefix: `AddonUpsellOption-${instanceId}`,
              // Disabled only for genuine stock reasons — never gated on
              // whether another group has been touched yet. Each group must
              // be independently selectable regardless of order.
              isAvailable: (value) => (hasOnlyDefault ? anyAvailable : this.valueIsAvailable(variants, group.index, value)),
              isChecked: (value) => added && selection[group.index] === value,
            }
          );

        // A single pill click both selects a variant and marks the row as
        // added; clicking the already-selected pill deselects it — that's
        // the only way to remove the accessory. Listens on 'click' AND
        // 'change' to cover both interaction paths: 'click' fires for every
        // mouse/Space activation (including re-clicking the already-checked
        // pill, which is how toggle-off is detected — 'change' never fires
        // for that case since the value doesn't actually change), while
        // 'change' covers pure keyboard arrow-key navigation between pills
        // in the same radio group, which moves the selection without ever
        // dispatching a 'click'. preventDefault() on click stops the native
        // check-toggle (and the 'change' it would otherwise trigger) so a
        // mouse click is only ever handled once, via the click branch.
        const handlePillActivation = (event, isClick) => {
          const target = event.target;
          const groupIndex = Number(target.dataset.optionIndex);
          const valueIndex = Number(target.dataset.valueIndex);
          const group = groups[groupIndex];
          const newValue = group && group.values[valueIndex];
          if (newValue === undefined) return;

          const isCurrentlySelected = hasOnlyDefault ? added : added && selection[groupIndex] === newValue;

          if (isCurrentlySelected) {
            // Re-selecting the same value never fires 'change', so toggle-off
            // is only ever reachable via a real click (mouse or Space).
            if (!isClick) return;
            added = false;
          } else {
            added = true;
            if (!hasOnlyDefault) {
              selection[groupIndex] = newValue;
              let match = this.findExactVariant(variants, selection);
              if (!match) {
                match =
                  variants.find((v) => v._labels[groupIndex] === newValue && v.available) ||
                  variants.find((v) => v.available) ||
                  variants[0];
                selection = match._labels.slice();
              }
              currentVariant = match;
            }
          }

          renderOptions();
          applySelection();
        };

        const renderOptions = () => {
          updatePriceDisplay();
          optionsWrap.innerHTML = groups.map((group) => renderGroup(group)).join('');

          optionsWrap.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.addEventListener('click', (event) => {
              event.preventDefault();
              handlePillActivation(event, true);
            });
            input.addEventListener('change', (event) => {
              handlePillActivation(event, false);
            });
          });
        };

        renderOptions();
        if (added) applySelection();

        return row;
      }

      hookForm() {
        const form = document.getElementById(this.formId);
        if (!form) {
          console.error('Add-on upsell: no form matched this.formId — add-ons cannot be intercepted at all.', this.formId);
          return;
        }
        this.form = form;
        this.productFormElement = form.closest('product-form');
        this.submitButtonEl = form.querySelector('[type="submit"]');

        // PRIMARY interception: the submit *button's click*, in the capture
        // phase, before the browser even begins the native form-submission
        // process. Cancelling the click's default action stops the 'submit'
        // event from ever being dispatched — so there is no listener-ordering
        // race with Dawn's own <product-form> submit handler to lose. (An
        // earlier version of this fix listened for 'submit' on document with
        // capture:true, which should have won that race in theory, but add-ons
        // still weren't reaching the cart in practice — intercepting one step
        // earlier, at the click, removes the race entirely rather than relying
        // on getting event-phase semantics exactly right.)
        if (this.submitButtonEl) {
          this.submitButtonEl.addEventListener(
            'click',
            (event) => {
              if (this.selected.size === 0) return; // nothing selected: let the native click/submit proceed untouched
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              this.handleSubmit(event);
            },
            true
          );
        } else {
          console.error('Add-on upsell: could not find the submit button inside the product form — add-ons cannot be intercepted.');
        }

        // FALLBACK: catches submission triggered without a button click (e.g.
        // pressing Enter in a form field). Still listens on document/capture so
        // it fires ahead of Dawn's own submit listener when it does apply.
        document.addEventListener(
          'submit',
          (event) => {
            if (event.target !== this.form) return;
            this.handleSubmit(event);
          },
          true
        );
      }

      async handleSubmit(event) {
        if (this.selected.size === 0) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const formData = new FormData(this.form);
        const mainId = Number(formData.get('id'));
        const mainQty = Number(formData.get('quantity')) || 1;
        if (!mainId || !Number.isFinite(mainId)) {
          console.error('Add-on upsell: main product variant id is missing or invalid, aborting.', formData.get('id'));
          return;
        }

        const items = [{ id: mainId, quantity: mainQty }];
        this.selected.forEach((variantId, handle) => {
          const id = Number(variantId);
          if (!id || !Number.isFinite(id)) {
            console.error(`Add-on upsell: skipping "${handle}" — resolved variant id is invalid.`, variantId);
            return;
          }
          items.push({ id, quantity: 1 });
        });

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const submitButton = this.form.querySelector('[type="submit"]');
        const spinner = this.form.querySelector('.loading__spinner');

        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        const body = {
          items,
          // The Cart Ajax API expects "sections" as a comma-separated string,
          // not a JSON array — sending an array left it unrecognized, so the
          // response had no `sections` payload and cart.renderContents() below
          // failed trying to read HTML for a section that was never returned.
          sections:
            cart && typeof cart.getSectionsToRender === 'function'
              ? cart.getSectionsToRender().map((section) => section.id).join(',')
              : '',
          sections_url: window.location.pathname,
        };

        try {
          // Always use the explicit .js suffix. window.routes.cart_add_url points
          // at the non-AJAX '/cart/add' endpoint (used by native form actions),
          // which responds with a full HTML page render of /cart rather than
          // JSON — that mismatch was silently succeeding server-side (items were
          // actually added) while breaking client-side JSON parsing here.
          const cartAddUrl = '/cart/add.js';

          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });

          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Could not add items to cart');
          }

          if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

          if (cart && typeof cart.renderContents === 'function') {
            try {
              cart.renderContents(result);
            } catch (renderError) {
              // The item(s) were already added successfully server-side at this
              // point — a failure here is only a UI-refresh problem, not a cart
              // problem. Fall back to a full navigation so the user still sees
              // the correct, current cart rather than a silently stuck button.
              console.error('Add-on upsell: cart updated but failed to refresh the cart UI', renderError);
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'addon-upsell',
              productVariantId: mainId,
              cartData: result,
            });
          }
        } catch (error) {
          console.error('Add-on upsell: cart add failed', error);
          this.showError(error.message);
        } finally {
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
          if (spinner) spinner.classList.add('hidden');
          if (cart && cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        }
      }

      showError(message) {
        const wrapper =
          this.productFormElement && this.productFormElement.querySelector('.product-form__error-message-wrapper');
        const messageEl = wrapper && wrapper.querySelector('.product-form__error-message');
        if (wrapper && messageEl) {
          messageEl.textContent = message || 'Something went wrong. Please try again.';
          wrapper.hidden = false;
        } else {
          alert(message || 'Something went wrong. Please try again.');
        }
      }
    }
  );
}
